import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  getStoredApiKey,
  setStoredApiKey,
  clearStoredApiKey,
  maskApiKey,
} from '../utils/apiKey';

interface ApiKeyContextType {
  hasKey: boolean;
  maskedKey: string | null;
  setApiKey: (key: string) => void;
  clearApiKey: () => void;
  showKeyModal: boolean;
  openKeyModal: () => void;
  closeKeyModal: () => void;
  showKeyRequiredModal: boolean;
  openKeyRequiredModal: () => void;
  closeKeyRequiredModal: () => void;
}

const ApiKeyContext = createContext<ApiKeyContextType | null>(null);

export const useApiKey = () => {
  const context = useContext(ApiKeyContext);
  if (!context) {
    throw new Error('useApiKey must be used within ApiKeyProvider');
  }
  return context;
};

export const ApiKeyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [hasKey, setHasKey] = useState(false);
  const [maskedKey, setMaskedKey] = useState<string | null>(null);
  const [showKeyModal, setShowKeyModal] = useState(false);
  const [showKeyRequiredModal, setShowKeyRequiredModal] = useState(false);

  const syncFromStorage = useCallback(() => {
    const stored = getStoredApiKey();
    setHasKey(!!stored);
    setMaskedKey(stored ? maskApiKey(stored) : null);
  }, []);

  useEffect(() => {
    syncFromStorage();
  }, [syncFromStorage]);

  const setApiKey = (key: string) => {
    const trimmed = key.trim();
    if (!trimmed) return;
    setStoredApiKey(trimmed);
    syncFromStorage();
  };

  const clearApiKey = () => {
    clearStoredApiKey();
    syncFromStorage();
  };

  const openKeyModal = () => setShowKeyModal(true);
  const closeKeyModal = () => setShowKeyModal(false);
  const openKeyRequiredModal = () => setShowKeyRequiredModal(true);
  const closeKeyRequiredModal = () => setShowKeyRequiredModal(false);

  return (
    <ApiKeyContext.Provider
      value={{
        hasKey,
        maskedKey,
        setApiKey,
        clearApiKey,
        showKeyModal,
        openKeyModal,
        closeKeyModal,
        showKeyRequiredModal,
        openKeyRequiredModal,
        closeKeyRequiredModal,
      }}
    >
      {children}
    </ApiKeyContext.Provider>
  );
};
